/**
 * use-ai-credits.ts — Hook cho "AI hai chế độ" (AI của eCDP trừ credit / Key của tôi):
 * nguồn theo nhà cung cấp, danh mục model, ví credit, sổ cái, mua credit, socket `ai:credits`.
 * Hợp đồng: docs/AI-2-CHE-DO-HOP-DONG-API.md.
 *
 * Backend làm song song → API có thể chưa có: query trả `null` khi 404 (ẩn phần mới,
 * giữ giao diện cũ) thay vì báo lỗi.
 */
import { useEffect } from 'react'
import {
  keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useMyPermissions } from '@/hooks/use-settings'
import { getSocket } from '@/lib/socket'
import {
  isApiMissing,
  type AiCredits, type AiModelsResponse, type AiSource, type AiSources, type CreditPack,
  type EmbeddingOverage, type LedgerItem, type WalletStatus,
} from '@/lib/ai-credits'
import type { CheckoutResult, PaymentMethod, Quote } from '@/hooks/use-billing'
import { aiKeys } from '@/hooks/use-ai'

export const aiCreditKeys = {
  sources: ['ai', 'sources'] as unknown[],
  models: ['ai', 'models'] as unknown[],
  credits: ['ai', 'credits'] as unknown[],
  ledger: ['ai', 'credits', 'ledger'] as unknown[],
  packs: ['billing', 'ai-credit-packs'] as unknown[],
  quote: (packId: string | null, promo?: string): unknown[] => ['billing', 'ai-credits', 'quote', packId, promo ?? ''],
}

/** GET trả null khi API chưa có (404) — phần giao diện mới tự ẩn. */
async function getOptional<T>(url: string, params?: Record<string, unknown>): Promise<T | null> {
  try {
    const { data } = await api.get<T>(url, { params })
    return data
  } catch (err) {
    if (isApiMissing(err)) return null
    throw err
  }
}

const optionalQuery = { retry: false as const, staleTime: 30_000 }

export function useAiSources() {
  return useQuery<AiSources | null>({
    queryKey: aiCreditKeys.sources,
    queryFn: () => getOptional<AiSources>('/ai/sources'),
    ...optionalQuery,
  })
}

export function useAiModels() {
  return useQuery<AiModelsResponse | null>({
    queryKey: aiCreditKeys.models,
    queryFn: () => getOptional<AiModelsResponse>('/ai/models'),
    ...optionalQuery,
  })
}

/**
 * Danh mục model XEM TRƯỚC theo một nguồn cho mọi nền tảng (`GET /ai/models?source=`) — màn Nguồn AI: chọn loại nguồn rồi
 * mới hiện model. AI của eCDP: chỉ nền tảng eCDP đang bật + model đã mở bán. Khoá nằm dưới `aiCreditKeys.models`.
 */
export function useAiModelsBySource(source: AiSource | null) {
  return useQuery<AiModelsResponse | null>({
    queryKey: [...aiCreditKeys.models, 'source', source],
    enabled: !!source,
    queryFn: () => getOptional<AiModelsResponse>('/ai/models', { source }),
    ...optionalQuery,
  })
}

export function useAiCredits(enabled = true) {
  return useQuery<AiCredits | null>({
    queryKey: aiCreditKeys.credits,
    enabled,
    queryFn: () => getOptional<AiCredits>('/ai/credits'),
    ...optionalQuery,
  })
}

export interface LedgerPage { items: LedgerItem[]; nextCursor: string | null }

export function useAiLedger(limit = 20, enabled = true) {
  return useInfiniteQuery<LedgerPage | null>({
    queryKey: [...aiCreditKeys.ledger, limit],
    enabled,
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      getOptional<LedgerPage>('/ai/credits/ledger', { limit, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: (last) => last?.nextCursor || undefined,
    retry: false,
  })
}

export function useCreditPacks(enabled = true) {
  return useQuery<{ packs: CreditPack[]; vatPercent: number } | null>({
    queryKey: aiCreditKeys.packs,
    enabled,
    queryFn: () => getOptional('/billing/ai-credit-packs'),
    ...optionalQuery,
    staleTime: 5 * 60_000,
  })
}

/** Báo giá chuẩn từ server (cùng dạng /billing/quote, có `vat`). */
export function useAiCreditQuote(packId: string | null, promoCode?: string) {
  return useQuery<Quote | null>({
    queryKey: aiCreditKeys.quote(packId, promoCode),
    enabled: !!packId,
    queryFn: async () => {
      try {
        const { data } = await api.post<Quote>('/billing/ai-credits/quote', { packId, ...(promoCode ? { promoCode } : {}) })
        return data
      } catch (err) {
        if (isApiMissing(err)) return null
        throw err
      }
    },
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60_000,
  })
}

function idempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c?.randomUUID) return c.randomUUID()
  return `ai-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** Tạo đơn mua credit — cùng dạng kết quả checkout gói (EvoPay / credit). */
export function useAiCreditCheckout() {
  return useMutation({
    mutationFn: async (body: {
      packId: string
      promoCode?: string
      method?: PaymentMethod
      invoice?: Record<string, unknown> | null
      returnTo?: string
    }) => {
      const { data } = await api.post<CheckoutResult>('/billing/ai-credits/checkout', body, {
        headers: { 'Idempotency-Key': idempotencyKey() },
      })
      return data
    },
  })
}

/** Đổi nguồn một/nhiều nhà cung cấp (+ nguồn mặc định khi thống nhất một nguồn). Trả như GET /ai/sources. */
export function useUpdateAiSources() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { providers: Record<string, AiSource>; defaultSource?: AiSource }) => {
      const { data } = await api.put<AiSources>('/ai/sources', body)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(aiCreditKeys.sources, data)
      // Đổi nguồn → khoá/giá model và nguồn thật của nhân viên AI đổi theo.
      void qc.invalidateQueries({ queryKey: aiCreditKeys.models })
      void qc.invalidateQueries({ queryKey: aiKeys.bots })
    },
  })
}

export function useUpdateCreditSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { dailyCap?: number | null; embeddingOverage?: EmbeddingOverage }) => {
      const { data } = await api.put('/ai/credits/settings', body)
      return data as AiCredits | null
    },
    onSuccess: (data) => {
      // Server trả như GET /ai/credits → cập nhật ngay; dạng khác → tải lại.
      if (data && typeof data === 'object' && 'balance' in data) qc.setQueryData(aiCreditKeys.credits, data)
      else void qc.invalidateQueries({ queryKey: aiCreditKeys.credits })
    },
  })
}

/** Sau khi đổi key BYOK: nguồn + model + bot đều phải tải lại. */
export function invalidateAiSourceData(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: aiCreditKeys.sources }),
    qc.invalidateQueries({ queryKey: aiCreditKeys.models }),
    qc.invalidateQueries({ queryKey: aiKeys.bots }),
  ])
}

/**
 * Lắng nghe `ai:credits` → cập nhật số dư/trạng thái ngay, rồi tải lại ví + sổ cái
 * (và danh sách bot vì "Đang tạm dừng" phụ thuộc số dư).
 */
export function useAiCreditsSocket() {
  const qc = useQueryClient()
  useEffect(() => {
    const socket = getSocket()
    const onCredits = (p: { balance?: number; status?: WalletStatus }) => {
      qc.setQueryData<AiCredits | null>(aiCreditKeys.credits, (cur) =>
        cur ? { ...cur, ...(typeof p?.balance === 'number' ? { balance: p.balance } : {}), ...(p?.status ? { status: p.status } : {}) } : cur,
      )
      void qc.invalidateQueries({ queryKey: aiCreditKeys.credits })
      void qc.invalidateQueries({ queryKey: aiCreditKeys.ledger })
      void qc.invalidateQueries({ queryKey: aiKeys.bots })
    }
    socket.on('ai:credits', onCredits)
    return () => {
      socket.off('ai:credits', onCredits)
    }
  }, [qc])
}

/**
 * Quyền mua lượt AI (`ai_credits.create` — Đội ngũ → Vai trò & quyền), khớp billing-routes.ts. Gói dịch vụ / mua thêm vẫn chỉ
 * chủ sở hữu / quản trị. Đang tải quyền → coi như chưa có (không chớp hiện nút).
 */
export function useCanBuyAiCredits(): boolean {
  return !!useMyPermissions().data?.has('ai_credits.create')
}
